// WindowSelector — the single global time-window segmented control (24H · 7D ·
// 30D · MTD · QTD · YTD). Drives the whole page: picking a window updates
// provider state, which re-requests the feed with the `window` param. Windows
// not in `available` (from meta.available_windows) are greyed + non-interactive
// so we never show empty cards for a window the data plane can't serve.
//
// Sparsity: a window present in `coverage` (backend's `window_coverage`, only
// for sparse windows) gets a small muted corner dot + a tooltip stating the
// traded/calendar split. The dot is absolutely positioned so it adds NO width
// and never re-centres the label — dense tokens and older payloads (empty
// coverage) render exactly as before.

import { Stack, Box, Tooltip } from '@mui/material';

import { tokens } from '../../../../styles/tokens';
import {
  WINDOW_OPTIONS,
  WindowCoverageMap,
  WindowKey,
} from '../../../../providers/options-flow/options-flow.helpers';

interface WindowSelectorProps {
  value: WindowKey;
  available: WindowKey[];
  onChange: (window: WindowKey) => void;
  // Sparse-window coverage; absent/empty => no markers (dense token / old feed).
  coverage?: WindowCoverageMap;
}

// The exact tooltip copy, e.g. "Sparse — 151 of 215 days traded".
const sparseTooltip = (cell: { trading_days: number; calendar_days: number }): string =>
  `Sparse — ${cell.trading_days} of ${cell.calendar_days} days traded`;

export const WindowSelector = ({ value, available, coverage, onChange }: WindowSelectorProps) => (
  <Stack
    direction="row"
    role="tablist"
    aria-label="Time window"
    sx={{
      display: 'inline-flex',
      border: `1px solid ${tokens.border.default}`,
      borderRadius: '6px',
      overflow: 'hidden',
    }}
  >
    {WINDOW_OPTIONS.map((o, i) => {
      const enabled = available.includes(o.key);
      const active = o.key === value;
      const cell = coverage?.[o.key];
      const toggle = (
        <Box
          key={o.key}
          role="tab"
          aria-selected={active}
          aria-disabled={!enabled}
          // Sparsity is otherwise mouse-only (dot is aria-hidden, tooltip is
          // hover-triggered); surface it to assistive tech on the tab itself.
          aria-label={cell ? `${o.label}, ${sparseTooltip(cell)}` : undefined}
          onClick={() => enabled && onChange(o.key)}
          sx={{
            position: 'relative',
            cursor: enabled ? 'pointer' : 'not-allowed',
            fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.4px',
            px: '11px',
            py: '4px',
            borderLeft: i === 0 ? 'none' : `1px solid ${tokens.border.default}`,
            color: active
              ? tokens.flow.accent
              : enabled
                ? tokens.text.secondary
                : tokens.text.muted,
            opacity: enabled ? 1 : 0.4,
            backgroundColor: active ? tokens.bg.widget : 'transparent',
            '&:hover': enabled ? { backgroundColor: tokens.bg.widget } : {},
          }}
        >
          {o.label}
          {cell && (
            // Absolutely positioned so the label stays centred and the toggle
            // keeps its width — no layout shift versus a dense toggle.
            <Box
              component="span"
              data-testid={`sparse-marker-${o.key}`}
              aria-hidden
              sx={{
                position: 'absolute',
                top: '3px',
                right: '3px',
                width: '3px',
                height: '3px',
                borderRadius: '50%',
                backgroundColor: tokens.text.muted,
                pointerEvents: 'none',
              }}
            />
          )}
        </Box>
      );

      return cell ? (
        <Tooltip key={o.key} title={sparseTooltip(cell)} placement="top" arrow>
          {toggle}
        </Tooltip>
      ) : (
        toggle
      );
    })}
  </Stack>
);
